import { z } from "zod";
import { useId } from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton, ScoreBar } from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ScoreGaugeSchema = z.object({
  score: z.number(),
  label: z.string(),
  maxScore: z.number().optional(),
  grade: z.string().optional(),
  breakdown: z
    .array(z.object({ label: z.string(), value: z.number() }))
    .optional(),
});

type ScoreGaugeData = z.infer<typeof ScoreGaugeSchema>;

/* ────────────────────── Helpers ────────────────────── */

function gaugeColor(ratio: number): string {
  if (ratio >= 0.7) return "hsl(var(--signal-success))";
  if (ratio >= 0.4) return "hsl(var(--signal-warning))";
  return "hsl(var(--signal-danger))";
}

// Gradient ID is now passed as a prop to avoid collisions

/* ────────────────────── SVG Gauge ────────────────────── */

interface CircleGaugeProps {
  score: number;
  maxScore: number;
  grade?: string;
  label: string;
  size?: number;
  gradientId: string;
}

function CircleGauge({
  score,
  maxScore,
  grade,
  label,
  size = 140,
  gradientId,
}: CircleGaugeProps) {
  const ratio = Math.min(score / maxScore, 1);
  const color = gaugeColor(ratio);
  const gradId = gradientId;

  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) * 0.78;
  const stroke = (size / 2) * 0.12;

  // Arc: 225deg start → 315deg end (270deg total sweep)
  const startAngle = 225;
  const sweepAngle = 270;
  const endAngle = startAngle + sweepAngle * ratio;

  function polarToCartesian(angle: number): [number, number] {
    const rad = ((angle - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  }

  function describeArc(start: number, end: number): string {
    const [sx, sy] = polarToCartesian(start);
    const [ex, ey] = polarToCartesian(end);
    const largeArc = end - start > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`;
  }

  const trackPath = describeArc(startAngle, startAngle + sweepAngle);
  const fillPath =
    ratio > 0
      ? describeArc(startAngle, Math.min(endAngle, startAngle + sweepAngle))
      : "";

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-label={`${label} ${score}점`}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.6" />
          <stop offset="100%" stopColor={color} stopOpacity="1" />
        </linearGradient>
      </defs>

      {/* Track */}
      <path
        d={trackPath}
        fill="none"
        stroke="hsl(var(--surface-sunken))"
        strokeWidth={stroke}
        strokeLinecap="round"
      />

      {/* Fill */}
      {ratio > 0 && (
        <path
          d={fillPath}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
        />
      )}

      {/* Score text */}
      <text
        x={cx}
        y={cy - (grade ? 6 : 4)}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={size * 0.22}
        fontWeight="700"
        fill={color}
      >
        {score}
      </text>

      {/* Grade */}
      {grade && (
        <text
          x={cx}
          y={cy + size * 0.17}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={size * 0.1}
          fontWeight="600"
          fill={color}
        >
          {grade}
        </text>
      )}
    </svg>
  );
}

/* ────────────────────── Component ────────────────────── */

export function ScoreGauge({
  widget,
  className,
}: AgentWidgetProps<ScoreGaugeData>) {
  const { score, label, maxScore = 100, grade, breakdown } = widget.data;
  const gradientId = useId();

  return (
    <div className={cn("w-full", className)}>
      {/* Gauge centered */}
      <div className="flex flex-col items-center gap-1">
        <CircleGauge
          score={score}
          maxScore={maxScore}
          grade={grade}
          label={label}
          gradientId={`gauge${gradientId}`}
        />
        <p className="text-[12px] font-medium text-text-primary">{label}</p>
        <p className="text-[10px] text-text-tertiary">
          {score} / {maxScore}
        </p>
      </div>

      {/* Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-stroke-subtle pt-4">
          {breakdown.map((item, idx) => (
            <ScoreBar
              key={idx}
              label={item.label}
              value={item.value}
              max={maxScore}
              showValue
              size="sm"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function ScoreGaugeSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-stroke-subtle bg-surface-raised p-5",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="size-[140px] rounded-full" />
        <Skeleton className="mt-1 h-3 w-24" />
        <Skeleton className="h-2.5 w-16" />
      </div>
      <div className="mt-4 space-y-2 border-t border-stroke-subtle pt-4">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-2.5 w-20 shrink-0" />
            <Skeleton className="h-1.5 flex-1 rounded-full" />
            <Skeleton className="h-2.5 w-6" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const scoreGaugeWidget = defineWidget<ScoreGaugeData>({
  type: WIDGET_TYPES.SCORE_GAUGE,
  label: "점수 게이지 (0~100)",
  schema: ScoreGaugeSchema,
  component: ScoreGauge,
  skeleton: ScoreGaugeSkeleton,
  defaultLayout: "half",
  maxHeight: 320,
  aiHint: "종합 점수를 원형 게이지로 표시하고 세부 항목을 바 차트로 분해",
});

registerWidget(scoreGaugeWidget);
